import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import managementService from '../api/managementService';

interface SystemSetting {
  key: string;
  value: any;
  description?: string;
  updatedAt: string;
  updatedBy?: {
    id: string;
    name: string;
    email: string;
  };
}

interface SettingConfig {
  key: string;
  label: string;
  description: string;
  type: 'boolean' | 'number' | 'text';
  defaultValue: any;
  min?: number;
  max?: number;
  step?: number;
  category: string;
}

const SETTING_CONFIGS: SettingConfig[] = [
  {
    key: 'aggregate_refresh.auto_trigger_enabled',
    label: 'Auto-Trigger Enabled',
    description: 'Automatically trigger aggregate refresh jobs when historical data is ingested',
    type: 'boolean',
    defaultValue: true,
    category: 'Auto-Trigger'
  },
  {
    key: 'aggregate_refresh.historical_threshold_hours',
    label: 'Historical Threshold (Hours)',
    description: 'Number of hours after which data is considered historical and triggers refresh jobs',
    type: 'number',
    defaultValue: 1,
    min: 0.5,
    max: 24,
    step: 0.5,
    category: 'Auto-Trigger'
  },
  {
    key: 'aggregate_refresh.default_batch_size_days',
    label: 'Default Batch Size (Days)',
    description: 'Default number of days to process in each batch for refresh jobs',
    type: 'number',
    defaultValue: 1,
    min: 1,
    max: 30,
    step: 1,
    category: 'Job Configuration'
  },
  {
    key: 'aggregate_refresh.default_delay_seconds',
    label: 'Default Delay (Seconds)',
    description: 'Default delay between batches to manage CPU load during refresh jobs',
    type: 'number',
    defaultValue: 5,
    min: 0,
    max: 60,
    step: 1,
    category: 'Job Configuration'
  },
  {
    key: 'aggregate_refresh.max_concurrent_jobs',
    label: 'Max Concurrent Jobs',
    description: 'Maximum number of refresh jobs that can run simultaneously',
    type: 'number',
    defaultValue: 3,
    min: 1,
    max: 10,
    step: 1,
    category: 'Performance'
  },
  {
    key: 'aggregate_refresh.job_retention_days',
    label: 'Job Retention (Days)',
    description: 'Number of days to keep completed refresh jobs before cleanup',
    type: 'number',
    defaultValue: 30,
    min: 1,
    max: 365,
    step: 1,
    category: 'Cleanup'
  }
];

export default function SystemSettings() {
  const { } = useTranslation();
  const queryClient = useQueryClient();
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});

  // Fetch all settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => managementService.systemSettings.getAll(),
  });

  // Initialize settings mutation
  const initializeSettingsMutation = useMutation({
    mutationFn: () => managementService.systemSettings.initialize(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      alert('Default settings initialized successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to initialize settings:', error);
      alert(`Failed to initialize settings: ${error.response?.data?.error || error.message}`);
    },
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: ({ key, value, description }: { key: string; value: any; description?: string }) =>
      managementService.systemSettings.update(key, { value, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
    onError: (error: any) => {
      console.error('Failed to update setting:', error);
      alert(`Failed to update setting: ${error.response?.data?.error || error.message}`);
    },
  });

  // Convert settings array to map for easy lookup
  const settingsMap = React.useMemo(() => {
    const map: Record<string, SystemSetting> = {};
    if (settings) {
      for (const setting of settings) {
        map[setting.key] = setting;
      }
    }
    return map;
  }, [settings]);

  // Group settings by category
  const settingsByCategory = React.useMemo(() => {
    const groups: Record<string, SettingConfig[]> = {};
    for (const config of SETTING_CONFIGS) {
      if (!groups[config.category]) {
        groups[config.category] = [];
      }
      groups[config.category].push(config);
    }
    return groups;
  }, []);

  const handleValueChange = (key: string, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSetting = async (config: SettingConfig) => {
    const pendingValue = pendingChanges[config.key];
    if (pendingValue === undefined) return;

    await updateSettingMutation.mutateAsync({
      key: config.key,
      value: pendingValue,
      description: config.description
    });

    // Remove from pending changes
    setPendingChanges(prev => {
      const updated = { ...prev };
      delete updated[config.key];
      return updated;
    });
  };

  const handleResetSetting = (config: SettingConfig) => {
    setPendingChanges(prev => {
      const updated = { ...prev };
      delete updated[config.key];
      return updated;
    });
  };

  const getCurrentValue = (config: SettingConfig) => {
    if (pendingChanges[config.key] !== undefined) {
      return pendingChanges[config.key];
    }
    return settingsMap[config.key]?.value ?? config.defaultValue;
  };

  const hasPendingChange = (config: SettingConfig) => {
    return pendingChanges[config.key] !== undefined;
  };

  const renderSettingInput = (config: SettingConfig) => {
    const currentValue = getCurrentValue(config);
    const hasChanges = hasPendingChange(config);

    switch (config.type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleValueChange(config.key, !currentValue)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                currentValue ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  currentValue ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm ${currentValue ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
              {currentValue ? 'Enabled' : 'Disabled'}
            </span>
            {hasChanges && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                Unsaved
              </span>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="number"
              value={currentValue}
              onChange={(e) => handleValueChange(config.key, parseFloat(e.target.value))}
              min={config.min}
              max={config.max}
              step={config.step}
              className={`block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                hasChanges ? 'border-orange-300 bg-orange-50' : ''
              }`}
            />
            {config.min !== undefined && config.max !== undefined && (
              <span className="text-xs text-gray-500">
                ({config.min} - {config.max})
              </span>
            )}
            {hasChanges && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                Unsaved
              </span>
            )}
          </div>
        );

      case 'text':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={currentValue}
              onChange={(e) => handleValueChange(config.key, e.target.value)}
              className={`block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                hasChanges ? 'border-orange-300 bg-orange-50' : ''
              }`}
            />
            {hasChanges && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                Unsaved
              </span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-sm text-gray-500">
            Configure system behavior and aggregate refresh parameters
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => initializeSettingsMutation.mutate()}
            disabled={initializeSettingsMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <PlayIcon className="h-4 w-4 mr-2" />
            {initializeSettingsMutation.isPending ? 'Initializing...' : 'Initialize Defaults'}
          </button>
        </div>
      </div>

      {/* Settings by Category */}
      {Object.entries(settingsByCategory).map(([category, configs]) => (
        <div key={category} className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
              <CogIcon className="h-5 w-5 text-gray-400 mr-2" />
              {category}
            </h3>
            
            <div className="space-y-6">
              {configs.map((config) => {
                const setting = settingsMap[config.key];
                const hasChanges = hasPendingChange(config);
                
                return (
                  <div key={config.key} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            {config.label}
                          </h4>
                          {!setting && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Not Set
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-3">
                          {config.description}
                        </p>
                        
                        {renderSettingInput(config)}
                        
                        {setting?.updatedAt && (
                          <p className="text-xs text-gray-400 mt-2">
                            Last updated: {new Date(setting.updatedAt).toLocaleString()}
                            {setting.updatedBy && ` by ${setting.updatedBy.name}`}
                          </p>
                        )}
                      </div>
                      
                      {hasChanges && (
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleSaveSetting(config)}
                            disabled={updateSettingMutation.isPending}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Save
                          </button>
                          <button
                            onClick={() => handleResetSetting(config)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Reset
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Auto-Trigger System:</strong> When enabled, the system automatically detects historical data 
              during ingestion and creates refresh jobs to process aggregate data. Changes to these settings take 
              effect immediately for new operations.
            </p>
            <div className="mt-2">
              <button
                onClick={() => window.open('/management/refresh-jobs', '_blank')}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                View active refresh jobs →
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Failed to load system settings. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}